// Mirrors EPN.Identity.Api's BusinessProfileSetupContracts.cs, WorkLocationContracts.cs, and
// MembershipContracts.cs, plus EPN.PayrollConfig.Api's PayScheduleContracts.cs and
// EPN.PaymentProcessing.Api's FundingAccountContracts.cs, exactly — see BusinessProfileSetupGateway.ts for
// which service each type/endpoint belongs to.

export type WizardStepKey = 'basic-info' | 'address' | 'banking' | 'payroll' | 'work-locations' | 'admin-info';

export const WIZARD_STEPS: { key: WizardStepKey; label: string }[] = [
  { key: 'basic-info', label: 'Basic Business Info' },
  { key: 'address', label: 'Business Address' },
  { key: 'banking', label: 'Bank Information' },
  { key: 'payroll', label: 'Payroll Setup' },
  { key: 'work-locations', label: 'Branches / Work Locations' },
  { key: 'admin-info', label: 'Admin Information' },
];

// ---- Step 1: Basic Business Info (Identity.Api) ----

export interface BasicInfoDraft {
  name: string | null;
  fein: string | null;
  email: string | null;
  phone: string | null;
  sein: string | null;
  website: string | null;
}

export interface BasicInfoStepResponse {
  isComplete: boolean;
  name: string | null;
  fein: string | null;
  email: string | null;
  phone: string | null;
  sein: string | null;
  website: string | null;
  draft: BasicInfoDraft | null;
}

export type SaveBasicInfoRequest = BasicInfoDraft;

// ---- Step 2: Business Address (Identity.Api) ----

export interface AddressDraft {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  county: string | null;
  mailingSameAsBilling: boolean | null;
  mailingAddressLine1: string | null;
  mailingAddressLine2: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingZip: string | null;
  mailingCountry: string | null;
  mailingCounty: string | null;
}

export interface AddressStepResponse {
  isComplete: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  county: string | null;
  mailingSameAsBilling: boolean;
  mailingAddressLine1: string | null;
  mailingAddressLine2: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingZip: string | null;
  mailingCountry: string | null;
  mailingCounty: string | null;
  draft: AddressDraft | null;
}

export interface SaveAddressRequest {
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  county: string | null;
  mailingSameAsBilling: boolean;
  mailingAddressLine1: string | null;
  mailingAddressLine2: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingZip: string | null;
  mailingCountry: string | null;
  mailingCounty: string | null;
}

export interface BusinessProfileResponse {
  companyId: string;
  status: 'Draft' | 'Completed';
  basicInfo: BasicInfoStepResponse;
  address: AddressStepResponse;
}

export interface SubmitBusinessProfileResponse {
  completed: boolean;
  completedAtUtc: string | null;
  missingSteps: string[];
}

// ---- Step 3: Bank Information (PaymentProcessing.Api) — Bank Partner is NEVER collected or shown here;
// it is derived server-side once Step 4's pay schedule is saved (approved architecture decision). ----

export interface CreateFundingAccountRequest {
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  accountType: string;
}

export interface FundingAccountResponse {
  id: string;
  bankName: string;
  maskedAccountLast4: string;
  accountType: string;
  fundingPartner: string | null;
  verificationState: string;
}

/** Step 3's own module-owned draft. Deliberately never carries a routing or account number — EPN never
 * persists raw banking secrets anywhere, not even in an unpromoted draft. Resuming this draft always
 * requires the Admin to re-enter Routing Number/Account Number. */
export interface SaveFundingAccountDraftRequest {
  bankName: string | null;
  accountType: string | null;
}

export type FundingAccountDraftResponse = SaveFundingAccountDraftRequest;

// ---- Step 4: Payroll Setup (PayrollConfig.Api) ----

// Matches PayFrequency.cs exactly (Weekly=1 .. Quarterly=5).
export const PAY_FREQUENCY = {
  Weekly: 1,
  BiWeekly: 2,
  SemiMonthly: 3,
  Monthly: 4,
  Quarterly: 5,
} as const;
export type PayFrequency = (typeof PAY_FREQUENCY)[keyof typeof PAY_FREQUENCY];

// Matches PayDayKind.cs exactly.
export const PAY_DAY_KIND = { DayOfMonth: 1, LastDayOfMonth: 2 } as const;
export type PayDayKind = (typeof PAY_DAY_KIND)[keyof typeof PAY_DAY_KIND];

export interface CreatePayScheduleRequest {
  name: string;
  frequency: PayFrequency;
  firstPayDay: number | null;
  firstPayDayKind?: PayDayKind;
  secondPayDay?: number | null;
  secondPayDayKind?: PayDayKind | null;
  payPeriodStartDate: string | null;
  /** Quarterly only — an actual "YYYY-MM-DD" date, never combined with firstPayDay/firstPayDayKind. */
  firstPayDate?: string | null;
}

export interface PayScheduleResponse {
  id: string;
  companyId: string;
  name: string;
  frequency: PayFrequency;
  firstPayDay: number | null;
  firstPayDayKind: PayDayKind;
  secondPayDay: number | null;
  secondPayDayKind: PayDayKind | null;
  payPeriodStartDate: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  firstPayDate: string | null;
}

/** Step 4's own module-owned draft. Every field is optional and none of it is sensitive. */
export interface SavePayScheduleDraftRequest {
  frequency: PayFrequency | null;
  firstPayDay: number | null;
  firstPayDayKind: PayDayKind | null;
  secondPayDay: number | null;
  secondPayDayKind: PayDayKind | null;
  payPeriodStartDate: string | null;
  firstPayDate: string | null;
}

export type PayScheduleDraftResponse = SavePayScheduleDraftRequest;

// ---- Step 5: Branches / Work Locations (Identity.Api) — matches WorkLocationType.cs exactly. The
// default/main location has locationType === null; it is never a selectable dropdown value. ----

export const WORK_LOCATION_TYPE = { Branch: 1, RemoteHub: 2, ClientSite: 3, Temporary: 4 } as const;
export type WorkLocationTypeValue = (typeof WORK_LOCATION_TYPE)[keyof typeof WORK_LOCATION_TYPE];

export const WORK_LOCATION_TYPE_LABELS: Record<string, string> = {
  Branch: 'Branch',
  RemoteHub: 'Remote Hub',
  ClientSite: 'Client Site',
  Temporary: 'Temporary',
};

export interface WorkLocationResponse {
  id: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
  county: string | null;
  phone: string | null;
  /** "Branch" | "RemoteHub" | "ClientSite" | "Temporary" | null (null only for the default/main location). */
  locationType: string | null;
  locationCode: string;
  timeZone: string | null;
  status: 'Active' | 'Inactive' | 'Archived';
  isDefault: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
  duplicateNameWarning: string | null;
  /** True while the location is Active (available for employee assignment). NOT the same as tax-registration complete or payroll-ready. */
  activeForPayroll: boolean;
  /** Send back as `expectedVersion` when saving; a stale value is refused with a visible conflict. */
  version: number;
  mailingAddress: WorkLocationMailingAddress;
  /** The registration record in force, or null when none has been saved. Account numbers are masked, never full. */
  taxRegistration: TaxRegistrationResponse | null;
  /** A location with no saved record is "NotRegistered". */
  taxRegistrationStatus: TaxRegistrationStatusName;
  /** True only for a Registered record with its effective date and a state account number. It records that details are on file; it does not mean payroll is ready. */
  registrationComplete: boolean;
  registrationIssues: string[];
}

// ---- Step 5: Payroll Tax Registration (Identity.Api, WorkLocationContracts.cs). Account numbers are only ever returned masked. ----

export const TAX_REGISTRATION_STATUS = { Registered: 1, InProgress: 2, NotRegistered: 3, NotRequired: 4 } as const;
export type TaxRegistrationStatusValue = (typeof TAX_REGISTRATION_STATUS)[keyof typeof TAX_REGISTRATION_STATUS];
export type TaxRegistrationStatusName = keyof typeof TAX_REGISTRATION_STATUS;

export const TAX_REGISTRATION_LABELS: Record<TaxRegistrationStatusName, string> = {
  Registered: 'Registered',
  InProgress: 'In Progress',
  NotRegistered: 'Not Registered',
  NotRequired: 'Not Required',
};

export interface WorkLocationMailingAddress {
  sameAsPhysical: boolean;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}

export interface TaxRegistrationResponse {
  status: TaxRegistrationStatusName;
  /** YYYY-MM-DD */
  effectiveDate: string | null;
  nexusEstablishedDate: string | null;
  stateAccountNumberMasked: string | null;
  suiAccountNumberMasked: string | null;
  sdiPflAccountNumberMasked: string | null;
  localTaxRegistrationMasked: string | null;
  localTaxAccountNumberMasked: string | null;
  notes: string | null;
  recordedAtUtc: string;
}

/** For every account-number field: `null` keeps the number already on file, `''` clears it, anything else replaces it. */
export interface SaveTaxRegistrationRequest {
  status: TaxRegistrationStatusValue;
  effectiveDate: string | null;
  nexusEstablishedDate: string | null;
  stateAccountNumber: string | null;
  suiAccountNumber: string | null;
  sdiPflAccountNumber: string | null;
  localTaxRegistration: string | null;
  localTaxAccountNumber: string | null;
  notes: string | null;
}

export interface WorkLocationRequest {
  name: string;
  locationType: WorkLocationTypeValue;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
  county: string | null;
  phone: string | null;
  /** Defaults to true on the server when omitted. */
  activeForPayroll?: boolean;
  /** Omitted or `sameAsPhysical: true` means the mailing address is the physical address. */
  mailingAddress?: WorkLocationMailingAddress;
  /** Omitted leaves the registration record unchanged. */
  taxRegistration?: SaveTaxRegistrationRequest;
  /** Update only: the `version` the form loaded. A stale value is refused with a 409 `WORK_LOCATION_VERSION_CONFLICT`. */
  expectedVersion?: number;
}

/** Step 5's own module-owned draft for the Add/Edit Location sub-form — one row per company. Null
 * `editingLocationId` means the draft is for a brand-new location; set means it's mid-edit of an existing
 * one. None of this data is sensitive. */
export interface SaveWorkLocationDraftRequest {
  editingLocationId: string | null;
  name: string | null;
  locationType: WorkLocationTypeValue | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  county: string | null;
  phone: string | null;
}

export interface WorkLocationDraftResponse {
  editingLocationId: string | null;
  name: string | null;
  /** "Branch" | "RemoteHub" | "ClientSite" | "Temporary" | null — see WorkLocationResponse.locationType. */
  locationType: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  county: string | null;
  phone: string | null;
}

// ---- Step 6: Admin Information (Identity.Api) ----

export interface MembershipResponse {
  id: string;
  userId: string;
  email: string;
  roleId: string;
  roleName: string;
  isActive: boolean;
  isOwner: boolean;
  /** The member's own profile values (Step 6 shows an Admin by name). Null when never set; absent from older servers. */
  displayName?: string | null;
  officeNumber?: string | null;
  phone?: string | null;
}

export interface CreateMembershipInvitationRequest {
  email: string;
  name: string | null;
  roleId: string;
  isOwner: boolean;
  officeNumber: string | null;
  phone: string | null;
}

export interface MembershipInvitationResponse {
  id: string;
  inviteeEmail: string;
  name: string | null;
  roleId: string;
  isOwner: boolean;
  officeNumber: string | null;
  phone: string | null;
  status: string;
  expiresAtUtc: string;
}

export interface RoleResponse {
  id: string;
  name: string;
  isSystemRole: boolean;
}
